'use client';

import Image from 'next/image';
import { Fragment, ReactNode, useState } from 'react';

import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonVariant,
    Column,
    Columns,
    DirectionEnum,
    Divider,
    FlexBox,
    Icon,
    IconName,
    IconSize,
    Image as ImageTrilogy,
    Row,
    Rows,
    Spacer,
    SpacerSize,
    Sticker,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyAlign,
} from '@bytel/trilogy-react';

import { PlanType } from '@app-types/product';

import { Cms } from '@components/cms';

import { useHome } from '@landing/hooks';

import { RoamingModal } from './roaming-modal';

interface ProductDetailProps {
    product: PlanType | null;
}

export function ProductDetail({ product }: Readonly<ProductDetailProps>) {
    const { selectedOption } = useHome();
    const [isModalOpen, setIsModalOpen] = useState(false);
    if (!product) {
        return null;
    }
    const is5gProduct = !!selectedOption?.gencode || product.maxConnections === '5g';
    const addedPepites = product.addedPepites?.filter((pepite) => pepite.pepite) ?? [];
    const bundlePepites = product.bundle_add_pepites?.filter((pepite) => pepite.pepite) ?? [];

    const renderSticker = () =>
        product.radioFlag ? <Sticker small variant={product.radioFlagColor} label={product.radioFlag} /> : null;

    const getModalTitle = () => {
        if (product.roamingPopinTitle) {
            return product.roamingPopinTitle;
        }
        if (product.roamingDestinations) {
            return 'Destinations incluses';
        }
        return 'Destinations en Europe / DOM';
    };

    return (
        <>
            {product.bundle_image && (
                <Box shadowless className='is-marginless product-detail-pack'>
                    <BoxContent backgroundColor={TrilogyColor.MAIN_FADE}>
                        <FlexBox align={Align.CENTER} direction={DirectionEnum.COLUMN}>
                            <ImageTrilogy
                                src={product.bundle_image.public_url}
                                alt=''
                                className='product-detail-special-image'
                            />
                            <Title typo={TypographyAlign.TEXT_CENTERED} level={TitleLevels.SIX} marginless>
                                {product.bundle_description_offer}
                            </Title>
                            <Spacer size={SpacerSize.ONE} />
                        </FlexBox>
                    </BoxContent>
                </Box>
            )}
            <Box shadowless className='is-marginless'>
                <BoxContent backgroundColor={TrilogyColor.BACKGROUND}>
                    <Rows gap={0}>
                        <Row>
                            <Rows gap={3}>
                                <Feature
                                    iconName={is5gProduct ? IconName.UI_5G : IconName.UI_4G}
                                    sticker={product.stickerPosition === 1 && renderSticker()}
                                >
                                    {product.networkText}
                                </Feature>

                                <Feature
                                    iconName={IconName.AIR_TRAVEL}
                                    sticker={product.stickerPosition === 2 && renderSticker()}
                                >
                                    <strong>{product.roamingEnvelope}&nbsp;</strong>
                                    {product.imagesRoaming ? (
                                        <span>
                                            {product.fromAbroad}
                                            {product.imagesRoaming?.map((image) => (
                                                <Fragment key={image.public_url}>
                                                    {' '}
                                                    <Image
                                                        key={image.public_url}
                                                        src={image.public_url}
                                                        width={16}
                                                        height={9}
                                                        alt={image.title}
                                                        style={{ verticalAlign: 'bottom' }}
                                                    />
                                                </Fragment>
                                            ))}
                                        </span>
                                    ) : (
                                        <Cms as='span' content={product.fromAbroad} />
                                    )}
                                </Feature>

                                <Feature
                                    iconName={IconName.PHONE_INFINITY}
                                    sticker={product.stickerPosition === 3 && renderSticker()}
                                >
                                    <Cms as='span' content={product.smsMms} />
                                </Feature>
                                {addedPepites.map((pepite, index) => (
                                    <Feature
                                        key={pepite.pepite}
                                        iconName={pepite.icon ?? IconName.PHONE_INFINITY}
                                        sticker={product.stickerPosition === 4 + index && renderSticker()}
                                    >
                                        <Cms as='span' content={pepite.pepite ?? ''} />
                                    </Feature>
                                ))}
                                {bundlePepites.length > 0 && <Divider marginless />}
                                {bundlePepites.map((pepite) => (
                                    <Feature
                                        key={`bundle-${pepite.icon_trilogy}-${pepite.pepite}`}
                                        iconName={(pepite.icon_trilogy as IconName) ?? IconName.PHONE_INFINITY}
                                        sticker={null}
                                    >
                                        <Cms as='span' content={pepite.pepite} />
                                    </Feature>
                                ))}
                            </Rows>
                        </Row>
                    </Rows>
                </BoxContent>
            </Box>
            <Box shadowless className='is-paddingless'>
                <BoxContent>
                    <Rows gap={3}>
                        <Row>
                            <Button fullwidth variant={ButtonVariant.GHOST} onClick={() => setIsModalOpen(true)}>
                                Voir les détails
                            </Button>
                            <Text marginless typo={TypographyAlign.TEXT_LEFT} level={TextLevels.FOUR}>
                                {product.textLinkPopin ?? '*\u00A0Liste des destinations dans "Voir les détails".'}
                            </Text>
                            <RoamingModal
                                active={isModalOpen}
                                onClose={() => setIsModalOpen(false)}
                                title={getModalTitle()}
                                roamingDestinations={product.roamingDestinations}
                                bundlePopin={product.bundle_popin}
                                bundleImage={product.bundle_image}
                                bundleDescriptionOffer={product.bundle_description_offer}
                            />
                        </Row>
                    </Rows>
                </BoxContent>
            </Box>
        </>
    );
}

const Feature = ({ iconName, children, sticker }: { iconName: IconName; children: ReactNode; sticker: ReactNode }) => {
    return (
        <Row>
            <Columns mobile gap={4} verticalAlign={Align.CENTER}>
                <Column narrow>
                    <Icon name={iconName} size={IconSize.SMALL} />
                </Column>
                <Column>
                    {sticker}
                    <Text level={TextLevels.TWO} marginless typo={TypographyAlign.TEXT_LEFT}>
                        {children}
                    </Text>
                </Column>
            </Columns>
        </Row>
    );
};
